import { UserRepository } from "@amityco/ts-sdk";

import { Button, ColumnModal, PageLoader } from "@/components";
import { useAmityAuth, useTranslate } from "@/providers/context";
import { useCallback, useEffect, useState } from "react";
import { CommunityUserAvatar } from "../Community/components";

const UnblockButton = ({
  userId,
  onSuccess,
}: {
  userId: string;
  onSuccess: () => void;
}) => {
  const { translate } = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleUnblock = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      await UserRepository.Relationship.unBlockUser(userId);
      onSuccess();
      setIsConfirming(false);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [userId, setIsError, setIsLoading, setIsConfirming, onSuccess]);

  return (
    <>
      <ColumnModal
        open={isConfirming}
        onOpenChange={setIsConfirming}
        triggerButton={
          <Button
            variant="tertiaryHighlight"
            className="ml-auto"
            onClick={() => setIsConfirming(true)}
          >
            {translate("Unblock")}
          </Button>
        }
        cols={3}
        title={translate("Unblock this user?")}
        primaryText={translate("Unblock")}
        confirmIsLoading={isLoading}
        confirmDisabled={isLoading}
        onConfirm={handleUnblock}
        onCancel={() => {
          setIsError(false);
          setIsConfirming(false);
        }}
      >
        <div className="p1-regular p-6 pt-0">
          {translate("They will be able to view and comment on your posts.")}
          {isError && (
            <div className="p2-regular mt-4 text-error-item">
              {`${translate("Failed to unblock user")}. ${translate("Please try again later")}.`}
            </div>
          )}
        </div>
      </ColumnModal>
    </>
  );
};

export const CommunityBlockedUsers = () => {
  const { authState } = useAmityAuth();
  const { translate } = useTranslate();
  const [blockedUsers, setBlockedUsers] = useState<Amity.User[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleGetBlockedUsers = useCallback<
    Amity.LiveCollectionCallback<Amity.InternalUser>
  >(
    async (info) => {
      setIsLoading(true);
      const { data, loading, error } = info;
      if (!loading) {
        setIsLoading(false);
        if (error) {
          setIsError(true);
        } else if (data) {
          setBlockedUsers(data);
        }
      }
    },
    [setBlockedUsers, setIsLoading, setIsError],
  );

  useEffect(() => {
    if (authState.state === "loading") {
      setIsLoading(true);
      return;
    } else {
      setIsLoading(false);
    }

    if (authState.state === "failed") {
      setIsError(true);
      return;
    }

    if (blockedUsers || !authState.authenticatedUser) {
      return;
    }

    const unsubscribe = UserRepository.getBlockedUsers(
      { limit: 50 },
      handleGetBlockedUsers,
    );
    return unsubscribe;
  }, [
    blockedUsers,
    authState.state,
    authState.authenticatedUser,
    handleGetBlockedUsers,
  ]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <div className="p1-regular flex h-full items-center justify-center px-14 text-center text-item-secondary">
        {`${translate("Failed to load blocked users")}. ${translate("Please try again later")}.`}
      </div>
    );
  }

  if (!blockedUsers || blockedUsers.length === 0) {
    return (
      <div className="p1-regular flex h-full items-center justify-center px-14 text-center text-item-secondary">
        {translate("No blocked users.")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {blockedUsers.map((user) => (
        <div key={user.userId} className="flex items-center gap-3">
          <CommunityUserAvatar user={user} />
          <span className="p2-semibold ellipsis">{user.displayName}</span>
          <UnblockButton
            userId={user.userId}
            onSuccess={() => {
              setBlockedUsers(
                blockedUsers?.filter((u) => u.userId !== user.userId),
              );
            }}
          />
        </div>
      ))}
    </div>
  );
};
